import { Fragment, memo } from 'react';
import * as React from 'react';

import type { Recommender } from '@coursera/event-pulse-types';

import type { ScrollPageProps } from 'bundles/product-card/components/types/ProductCardV2';
import SearchPageMerchandisingCollection from 'bundles/search-common/components/experiments/merchInSearch/SearchPageMerchandisingCollection';
import { getGoogleAIRefreshProducts } from 'bundles/search-common/components/experiments/merchInSearch/merchandisingInSearchUtils';
import SearchCards from 'bundles/search-common/components/search-cards/SearchCards';
import type { SearchHit } from 'bundles/search-common/providers/searchTypes';
import {
  getShouldShowGoogleAISpotlightCollectionsInSearch,
  getShouldShowGoogleAISpotlightCollectionsInSearchWithImpression,
} from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';

type SearchPageBodySectionsProps = {
  hits: SearchHit[];
  resultsPerPage: number;
  forceOpenInNewTab: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  isLoading?: boolean;
  isSearchPage: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  cardIndexOffset?: number;
  setScrollPage?: (scrollPageProps: ScrollPageProps) => void;
};
const SearchPageBodySections = ({
  hits,
  resultsPerPage,
  forceOpenInNewTab,
  eventingData,
  isLoading,
  isSearchPage,
  onScrolledToBottom,
  totalHitsDisplayed,
  cardIndexOffset,
  setScrollPage,
}: SearchPageBodySectionsProps) => {
  const isConsumerSearch = useIsConsumerSerp();
  const shouldshowMerchandisingSearchCards = getShouldShowGoogleAISpotlightCollectionsInSearch();
  const searchHits = hits.filter(isSearchProductHit);
  const googleAIRefreshProducts = getGoogleAIRefreshProducts(searchHits);
  if (googleAIRefreshProducts && googleAIRefreshProducts.length > 0 && isConsumerSearch) {
    getShouldShowGoogleAISpotlightCollectionsInSearchWithImpression();
  }
  if (!hits || !Array.isArray(hits)) return null;

  const sections: Array<SearchHit[]> = [];

  for (let i = 0; i < hits.length; i += resultsPerPage) {
    sections.push(hits.slice(i, i + resultsPerPage));
  }

  return shouldshowMerchandisingSearchCards && isConsumerSearch ? (
    <Fragment>
      {sections.map((section, index) => (
        <Fragment key={`section-${section[0].id}`}>
          <SearchCards
            hits={section}
            resultsPerPage={resultsPerPage}
            forceOpenInNewTab={forceOpenInNewTab}
            eventingData={eventingData}
            isLoading={isLoading && index === sections.length - 1}
            isSearchPage={isSearchPage}
            onScrolledToBottom={onScrolledToBottom}
            totalHitsDisplayed={totalHitsDisplayed}
            cardIndexOffset={cardIndexOffset ? cardIndexOffset + index * resultsPerPage : undefined}
            setScrollPage={setScrollPage}
          />
          <SearchPageMerchandisingCollection
            hits={section}
            isLoading={isLoading ?? false}
            eventingData={eventingData}
            forceOpenInNewTab={forceOpenInNewTab}
          />
        </Fragment>
      ))}
    </Fragment>
  ) : (
    <SearchCards
      hits={hits}
      resultsPerPage={resultsPerPage}
      forceOpenInNewTab={forceOpenInNewTab}
      eventingData={eventingData}
      isLoading={isLoading}
      isSearchPage={isSearchPage}
      onScrolledToBottom={onScrolledToBottom}
      totalHitsDisplayed={totalHitsDisplayed}
      cardIndexOffset={cardIndexOffset}
      setScrollPage={setScrollPage}
    />
  );
};

export default memo(SearchPageBodySections);
